import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addDays } from 'date-fns'
import { Formik } from 'formik'
import { isEqual } from 'lodash'

import {
  DateTimePicker,
  MultiSelect,
  Textarea,
  Button,
  Checkbox,
  Select,
  SelectScheduleType,
  WagePremiums,
  BillingDetails,
  Attributes,
  FormRow,
  Form
} from 'components/Forms'
import BaseModal from 'components/Modals/BaseModal'
import { Breaks, IsShiftOver16Hours, UserInput } from 'components/Schedule'
import { Collapsable } from 'components/UI'
import { Parse } from 'services/Dates'
import { parseToTemplate } from 'services/ScheduleTemplates'
import { getParsedTagsForMultiselect } from 'services/getParsedTagsForMultiselect'
import { getAssignablePositions } from 'services/Structure'
import { getScheduleTypeInitialvalue } from 'services/schedule'
import { closeModal } from 'store/actions/modals'
import { updateEvents } from 'store/actions/scheduleTemplates'
import { Alert, Input } from 'ui'

import { t } from 'translations'

const ScheduleTemplateEventMultiEditModal = ({ ids }) => {
  const dispatch = useDispatch()
  const { projects } = useSelector(state => state.resources)
  const { enableWagePremiums, enableBillingDetails, enableShiftSpecificAttributes, projectIsRequired, breaksAreRequired } = useSelector(state => state.settings)
  const { events: templateEvents, days: numberOfDaysInTemplate, requiredPositions } = useSelector(state => state.scheduleTemplates)
  const showOtherInfo = projectIsRequired || breaksAreRequired

  const events = templateEvents
    .filter(({ id }) => ids.includes(id))
    .map(event => ({ ...event, projects: Array.isArray(event.projects) ? event.projects[0] : undefined }))

  const onSubmit = async values => {
    // Delete all keys that do not have a value set.
    for (const key in values) if (values[key] && values[key] === 'multipleValues') delete values[key]

    // Parse project field that is not an array in frontend
    if (values.projects && values.projects.length) values.projects = [values.projects]

    dispatch(updateEvents(events.map(event => parseToTemplate({ ...event, ...values }))))
    dispatch(closeModal())
  }

  // Function that sets the initial value of the form by checking if all the events have the same value
  const getInitialFieldValue = (field, events) => {
    const getFieldValue = fieldValue => (Array.isArray(fieldValue) ? fieldValue.join(',') : fieldValue)
    const baseValue = getFieldValue(events[0][field])
    const allSame = events.every(event => isEqual(getFieldValue(event[field]), baseValue))
    return allSame ? events[0][field] : 'multipleValues'
  }

  const MultipleValuesInput = ({ children, name = '', values, setFieldValue, module = 'manager.schedule.eventForm' }) => {
    if (values[name] === 'multipleValues')
      return (
        <div className="form-group">
          <label className="control-label">{t(`${module}.${name.toUpperCase()}.LABEL`)}</label>
          <div className="input-group mb-3">
            <Input disabled type="text" placeholder={t('manager.schedule.eventForm.MULTIPLE_VALUES')} />
            <div className="input-group-append">
              <Button
                className="btn btn-outline-secondary"
                text="manager.schedule.eventForm.CLICK_TO_EDIT"
                onClick={() => setFieldValue(name, events[0][name])}
              />
            </div>
          </div>
          <small className="form-text text-muted">{t(`${module}.${name.toUpperCase()}.DESCRIPTION`)}</small>
        </div>
      )
    return children
  }

  return (
    <BaseModal hideButtons title="manager.schedule.MULTI_EDIT_MODAL">
      <Formik
        enableReinitialize={true}
        validate={({ start, end, breaks }) => {
          let errors = {}
          if (![start, end].includes('multipleValues')) {
            const startDate = Parse(start, { inFormat: 'serverTime' })
            let endDate = Parse(end, { inFormat: 'serverTime' })
            if (startDate >= endDate) endDate = addDays(endDate, 1)
            if (startDate > endDate) errors.end = 'manager.bulletins.END_BEFORE_START'
            if (breaksAreRequired && !breaks) errors.breaks = 'manager.schedule.REQUIRED_FIELD'
            if (breaksAreRequired && breaks && breaks.length === 0) errors.breaks = 'manager.schedule.REQUIRED_FIELD'
          }
          return errors
        }}
        initialValues={{
          positionId: getInitialFieldValue('positionId', events),
          start: getInitialFieldValue('start', events),
          end: getInitialFieldValue('end', events),
          multiDayIndex: getInitialFieldValue('multiDayIndex', events),
          comment: getInitialFieldValue('comment', events),
          isExtraShift: getInitialFieldValue('isExtraShift', events),
          isOvertime: getInitialFieldValue('isOvertime', events),
          isOnCall: getInitialFieldValue('isOnCall', events),
          isIrreplaceable: getInitialFieldValue('isIrreplaceable', events),
          needsReplacement: getInitialFieldValue('needsReplacement', events),
          noEmployeeAvailable: getInitialFieldValue('noEmployeeAvailable', events),
          hasSpecificRequirements: getInitialFieldValue('hasSpecificRequirements', events),
          coveredOtherUnit: getInitialFieldValue('coveredOtherUnit', events),
          projects: getInitialFieldValue('projects', events),
          tags: getInitialFieldValue('tags', events),
          breaks: getInitialFieldValue('breaks', events),
          wagePremiums: getInitialFieldValue('wagePremiums', events) || [],
          billingDetails: getInitialFieldValue('billingDetails', events) || { billingId: '' },
          selectedScheduleType: getScheduleTypeInitialvalue(events)
        }}
        onSubmit={values => onSubmit(values)}
      >
        {({ values, status, setStatus, isSubmitting, setFieldValue }) => {
          // Props for the multiple values component
          const mvProps = { values, setFieldValue }
          return (
            <Form className="mt-2">
              {/* Error message */}
              <Alert variant={status?.type} onClose={() => setStatus()} show={!!status?.msg}>
                {status?.msg}
              </Alert>
              <IsShiftOver16Hours {...values} />

              {/* Form */}
              {/* Main info */}
              <MultipleValuesInput name="employeeId" {...mvProps}>
                <UserInput />
              </MultipleValuesInput>

              <MultipleValuesInput name="positionId" {...mvProps}>
                <MultiSelect
                  module="manager.schedule.eventForm"
                  name="positionId"
                  options={getAssignablePositions({ forMultiSelect: true, filterByIds: requiredPositions })}
                />
              </MultipleValuesInput>

              {/* TIME section */}
              <FormRow>
                <MultipleValuesInput name="start" {...mvProps}>
                  <DateTimePicker module="manager.schedule.eventForm" name="start" type="time" />
                </MultipleValuesInput>
                <MultipleValuesInput name="end" {...mvProps}>
                  <DateTimePicker module="manager.schedule.eventForm" name="end" type="time" />
                </MultipleValuesInput>
              </FormRow>
              {/* Radios for selecting shift type */}
              <MultipleValuesInput name="selectedScheduleType" {...mvProps}>
                <SelectScheduleType shifts={events} shift={values} setFieldValue={setFieldValue} isTemplate />
              </MultipleValuesInput>

              {/* This option is only shown in multi-day templates */}
              <MultipleValuesInput name="multiDayIndex" module="manager.scheduleTemplates.form" {...mvProps}>
                {numberOfDaysInTemplate > 1 ? (
                  <Select
                    module="manager.scheduleTemplates.form"
                    name="multiDayIndex"
                    options={new Array(numberOfDaysInTemplate)
                      .fill(1)
                      .map((option, index) => ({ _id: index, name: t('manager.scheduleTemplates.form.MULTIDAYINDEX.OPTION', { day: index + 1 }) }))}
                  />
                ) : null}
              </MultipleValuesInput>

              <MultipleValuesInput name="comment" {...mvProps}>
                <Textarea module="manager.schedule.eventForm" name="comment" />
              </MultipleValuesInput>

              {/* Shift options */}
              <Collapsable type="shiftOptions" title="manager.schedule.eventForm.SHIFT_OPTIONS">
                <MultipleValuesInput name="isExtraShift" {...mvProps}>
                  <Checkbox module="manager.schedule.eventForm" name="isExtraShift" />
                </MultipleValuesInput>
                <MultipleValuesInput name="isOvertime" {...mvProps}>
                  <Checkbox module="manager.schedule.eventForm" name="isOvertime" />
                </MultipleValuesInput>
                <MultipleValuesInput name="isOnCall" {...mvProps}>
                  <Checkbox module="manager.schedule.eventForm" name="isOnCall" />
                </MultipleValuesInput>
                <MultipleValuesInput name="isIrreplaceable" {...mvProps}>
                  <Checkbox module="manager.schedule.eventForm" name="isIrreplaceable" />
                </MultipleValuesInput>
                <MultipleValuesInput name="needsReplacement" {...mvProps}>
                  <Checkbox module="manager.schedule.eventForm" name="needsReplacement" />
                </MultipleValuesInput>
                <MultipleValuesInput name="noEmployeeAvailable" {...mvProps}>
                  <Checkbox module="manager.schedule.eventForm" name="noEmployeeAvailable" />
                </MultipleValuesInput>
                <MultipleValuesInput name="hasSpecificRequirements" {...mvProps}>
                  <Checkbox module="manager.schedule.eventForm" name="hasSpecificRequirements" />
                </MultipleValuesInput>
                <MultipleValuesInput name="coveredOtherUnit" {...mvProps}>
                  <Checkbox module="manager.schedule.eventForm" name="coveredOtherUnit" />
                </MultipleValuesInput>
              </Collapsable>

              {/* Extra info */}
              <Collapsable type="otherInfo" title="manager.schedule.eventForm.OTHER_INFO" show={showOtherInfo}>
                <MultipleValuesInput name="projects" {...mvProps}>
                  <MultiSelect
                    module="manager.schedule.eventForm"
                    name="projects"
                    options={projects.map(({ _id, code, name }) => ({ value: _id, label: `${name} [${code}]` }))}
                    isClearable
                    required={projectIsRequired}
                  />
                </MultipleValuesInput>
                <MultipleValuesInput name="tags" {...mvProps}>
                  <MultiSelect module="manager.schedule.eventForm" name="tags" options={getParsedTagsForMultiselect()} isClearable isMulti />
                </MultipleValuesInput>

                <MultipleValuesInput name="breaks" {...mvProps}>
                  <Breaks
                    event={{ start: values.start, end: values.end }}
                    setFieldValue={(...args) => setFieldValue(...args)}
                    breaks={values.breaks}
                    required={breaksAreRequired}
                  />
                </MultipleValuesInput>

                {enableWagePremiums ? (
                  <MultipleValuesInput name="wagePremiums" {...mvProps}>
                    <WagePremiums wagePremiums={values.wagePremiums} />
                  </MultipleValuesInput>
                ) : null}
                {enableShiftSpecificAttributes ? (
                  <MultipleValuesInput name="requiredAttributes" {...mvProps}>
                    <Attributes module="manager.schedule.eventForm" name="requiredAttributes" attributes={values.requiredAttributes} />
                  </MultipleValuesInput>
                ) : null}
              </Collapsable>
              {!!enableBillingDetails && (
                <Collapsable type="billingDetails" title="components.billingDetails.BILLING_DETAILS">
                  <MultipleValuesInput module="components.billingDetails" name="billingDetails" {...mvProps}>
                    <BillingDetails context="shift" />
                  </MultipleValuesInput>
                </Collapsable>
              )}
              <Button className="btn btn-primary mr-2" type="submit" isSubmitting={isSubmitting} text="SUBMIT" />
              <Button className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())}>
                {t(`actions.CLOSE`)}
              </Button>
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

export default ScheduleTemplateEventMultiEditModal
